import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output} from "@angular/core";
import {DealIncentive, VehicleIncentive} from "src/app/settings-module/models/incentives";
import {isEmpty, isNil} from "ramda";
import {DealService} from "src/app/clearpath-module/services";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {CustomPriceDialogComponent} from "../../dialogs/custom-price-dialog/custom-price-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {CustomStringDialogComponent} from "../../dialogs/custom-string-dialog/custom-string-dialog.component";

@Component({
  selector: "app-smw-rebates-form",
  templateUrl: "./smw-rebates-form.component.html",
  styleUrls: ["./smw-rebates-form.component.scss"]
})
export class SmwRebatesFormComponent implements OnInit, OnDestroy, OnChanges {

  private unsubscribe$ = new Subject();

  @Input() dealIncentives: DealIncentive[];
  @Input() vehicleIncentives: VehicleIncentive[];
  @Input() dealType: string;
  @Output() updateIncentives = new EventEmitter<DealIncentive[]>();

  rebates: any[];
  customRebateName: string | null | undefined = null;

  constructor(
    private dealService: DealService,
    private dialog: MatDialog
  ) {
  }

  ngOnInit() {
    this.rebates = this.getRebates();
    this.dealService.selectDeal()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(deal => {
        this.dealIncentives = deal.incentives || [];
        this.rebates = this.getRebates();
        if (this.dealIncentives.length > 0) {
          this.customRebateName = this.dealIncentives[0].customRebateName;
        }
      });
  }

  ngOnChanges() {
    this.rebates = this.getRebates();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onToggleAccept(rebate) {
    const ig = (this.dealIncentives || [])
      .find(i => i.incentiveGroupName === rebate.incentiveGroupName);
    const rebateIndex = this.rebates.findIndex(r => r.title === rebate.title);
    if (isNil(ig) || isEmpty(ig)) {
      const newGroup: DealIncentive = {
        incentiveGroupName: rebate.incentiveGroupName,
        [rebate.type]: rebate.value
      };
      this.rebates[rebateIndex].selected = true;
      this.dealIncentives.push(newGroup);
    } else {
      this.rebates[rebateIndex].selected = !this.rebates[rebateIndex].selected;
      if (ig[rebate.type]) {
        if (rebate.type === "customerCash") {
          ig.customerCashDisabled = !ig.customerCashDisabled;
        } else if (rebate.type === "bonusCash") {
          ig.bonusCashDisabled = !ig.bonusCashDisabled;
        } else if (rebate.type === "leaseCash") {
          ig.leaseCashDisabled = !ig.leaseCashDisabled;
        } else if (rebate.type === "financeCash") {
          ig.financeCashDisabled = !ig.financeCashDisabled;
        } else if (rebate.type === "customRebate") {
          ig.customRebateEnabled = !ig.customRebateEnabled;
        } else {
          delete ig[rebate.type];
        }
      } else {
        if (rebate.type === "customerCash") {
          ig.customerCashDisabled = false;
        } else if (rebate.type === "bonusCash") {
          ig.bonusCashDisabled = false;
        } else if (rebate.type === "leaseCash") {
          ig.leaseCashDisabled = true;
        } else if (rebate.type === "financeCash") {
          ig.financeCashDisabled = true;
        } else if (rebate.type === "customRebate") {
          ig.customRebateEnabled = true;
        }
        ig[rebate.type] = rebate.value;
      }
    }
    this.updateIncentives.emit(this.dealIncentives);
  }

  // UI & RENDERING

  getRebates() {
    const rebates = [];
    let ig;
    this.vehicleIncentives.forEach(vi => {

      ig = vi.incentiveGroupName;

      if (vi.incentive.cashDetails) {
        const cd = vi.incentive.cashDetails;

        if (cd.otherRebate) {
          if (ig !== "Toyota Lease Loyalty Program") {
            rebates.push({
              incentiveGroupName: ig,
              title: "Rebate",
              type: "otherRebate",
              value: cd.otherRebate,
              selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "otherRebate")
            });
          }
        }
        if (this.dealType !== 'lease' && cd.customerCash) {
          rebates.push({
            incentiveGroupName: ig,
            title: "Customer Cash",
            type: "customerCash",
            value: cd.customerCash,
            selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "customerCash")
          });
        }
        if (cd.bonusCash) {
          rebates.push({
            incentiveGroupName: ig,
            title: "Bonus Cash",
            type: "bonusCash",
            value: cd.bonusCash,
            selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "bonusCash")
          });
        }
        if (this.dealType === 'lease' && cd.leaseCash) {
          rebates.push({
            incentiveGroupName: ig,
            title: "Lease Cash",
            type: "leaseCash",
            value: cd.leaseCash,
            selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "leaseCash")
          });
        }
        if (this.dealType === 'finance' && cd.financeCash) {
          rebates.push({
            incentiveGroupName: ig,
            title: "Finance Cash",
            type: "financeCash",
            value: cd.financeCash,
            selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "financeCash")
          });
        }
      }

    });

    // these rebates are available for all vehicles and are associated
    // with the following incentive group.
    ig = "Toyota Portland Region Incentives";

    rebates.push({
      incentiveGroupName: ig,
      title: "Military Rebate",
      type: "militaryRebate",
      value: 500,
      selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "militaryRebate")
    });

    rebates.push({
      incentiveGroupName: ig,
      title: "College Rebate",
      type: "collegeRebate",
      value: 500,
      selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "collegeRebate")
    });

    rebates.push({
      incentiveGroupName: ig,
      title: "Finance Loyalty",
      type: "customerLoyalty500",
      value: 500,
      selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "customerLoyalty500"),
      disabled: this.dealType !== 'finance',
    });

    rebates.push({
      incentiveGroupName: ig,
      title: "Lease Loyalty",
      type: "customerLoyalty1000",
      value: 1000,
      selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "customerLoyalty1000"),
      disabled: this.dealType !== 'lease',
    });

    rebates.push({
      incentiveGroupName: ig,
      title: "Custom Rebate",
      type: "customRebate",
      value: this.dealIncentives?.[0]?.customRebate || 0,
      selected: this.isCashIncentiveSelected(this.dealIncentives, ig, "customRebate")
    });

    return rebates;
  }

  checkBoxColor(rebate): string {
    if (rebate.selected) {
      return "app-bg-success";
    }
    return "";
  }

  isCashIncentiveSelected(dealIncentives, incentiveGroupName, type): boolean {
    const inc = (dealIncentives || []).find(i => i.incentiveGroupName === incentiveGroupName);
    if (!inc) {
      return false;
    }
    if (type === "customerCash") {
      return !inc.customerCashDisabled;
    }
    if (type === "bonusCash") {
      return !inc.bonusCashDisabled;
    }
    if (type === "leaseCash") {
      return !inc.leaseCashDisabled;
    }
    if (type === "financeCash") {
      return !inc.financeCashDisabled;
    }
    if (type === "customRebate") {
      return inc.customRebateEnabled;
    }
    return !!inc[type];
  }

  isLeaseOffer(incentive) {
    return incentive.leaseOffer;
  }

  overrideCustomRebateAmount(rebate: any) {
    const dialogRef = this.dialog.open(CustomPriceDialogComponent, {
      width: '500px',
      autoFocus: true,
      data: {
        title: 'Override Custom Rebate Amount',
        originalPrice: 0,
        customPrice: rebate.value,
        maxPrice: 25000
      }
    });
    dialogRef.beforeClosed().subscribe(result => {
      if (result && this.dealIncentives?.length) {
        if (result.action === 'remove') {
          this.dealIncentives[0].customRebate = 0
        } else if (result.action === 'update') {
          this.dealIncentives[0].customRebate = result.value
        }
        this.updateIncentives.emit(this.dealIncentives);
      }
    });
  }

  overrideCustomRebateName() {
    const dialogRef = this.dialog.open(CustomStringDialogComponent, {
      width: '500px',
      autoFocus: true,
      data: {
        title: 'Override Custom Rebate Name',
        originalString: "Custom Rebate",
        customString: this.customRebateName
      }
    });
    dialogRef.beforeClosed().subscribe(result => {
      if (result && this.dealIncentives?.length) {
        if (result.action === 'remove') {
          this.dealIncentives[0].customRebateName = null
        } else if (result.action === 'update') {
          this.dealIncentives[0].customRebateName = result.value
        }
        this.updateIncentives.emit(this.dealIncentives);
      }
    });
  }
}
